import type { DateRangePickerProps } from '@risk-smart/themed-cloudscape-components/date-range-picker';
import dayjs from 'dayjs';

export interface ResolvedDateRange {
  start: Date;
  end: Date;
}

/**
 * Past-only relative ranges offered by the indicator results date range picker
 * (CUS-142). Future ("next") options are intentionally omitted — indicator
 * results are historical.
 */
export const INDICATOR_RESULTS_RELATIVE_OPTIONS: DateRangePickerProps.RelativeOption[] =
  [
    { key: 'last-1-week', amount: -1, unit: 'week', type: 'relative' },
    { key: 'last-1-month', amount: -1, unit: 'month', type: 'relative' },
    { key: 'last-3-months', amount: -3, unit: 'month', type: 'relative' },
    { key: 'last-6-months', amount: -6, unit: 'month', type: 'relative' },
    { key: 'last-1-year', amount: -1, unit: 'year', type: 'relative' },
  ];

/** Default selection on first load and the "Reset to default" target: Last 6 months. */
export const DEFAULT_INDICATOR_RESULTS_RANGE: DateRangePickerProps.Value = {
  key: 'last-6-months',
  amount: -6,
  unit: 'month',
  type: 'relative',
};

/**
 * Resolves a picker value (relative or absolute) into concrete start/end Date
 * bounds. `now` is injectable for deterministic tests.
 */
export const resolveDateRange = (
  value: DateRangePickerProps.Value | null,
  now: dayjs.Dayjs = dayjs()
): ResolvedDateRange | null => {
  if (!value) {
    return null;
  }

  if (value.type === 'relative') {
    // amount is negative for past ranges (e.g. -6 months).
    return {
      start: now.add(value.amount, value.unit).toDate(),
      end: now.toDate(),
    };
  }

  return {
    start: dayjs(value.startDate).startOf('day').toDate(),
    end: dayjs(value.endDate).endOf('day').toDate(),
  };
};

/**
 * Calendar predicate for the picker's `isDateEnabled`: future dates are not
 * selectable (CUS-142 confirmed decision — results are historical).
 */
export const isNotFutureDate = (date: Date, now: Date = new Date()): boolean =>
  !dayjs(date).isAfter(dayjs(now), 'day');

/**
 * Filters indicator results to the applied date range. With no range (feature
 * flag off) the original "last 1 year" window is preserved unchanged.
 */
export const filterResultsByDateRange = <T extends { ResultDate: string }>(
  data: T[],
  dateRange?: ResolvedDateRange
): T[] => {
  if (!dateRange) {
    const oneYearAgo = dayjs().subtract(1, 'year');

    return data.filter((d) => oneYearAgo.isBefore(new Date(d.ResultDate)));
  }

  return data.filter((d) => {
    const x = new Date(d.ResultDate);

    return x >= dateRange.start && x <= dateRange.end;
  });
};

/**
 * Analytics-facing description of an applied picker value: the relative option
 * key (e.g. "last-6-months") or "custom" for absolute ranges (CUS-145/146).
 */
export const describeDateRangeForAnalytics = (
  value: DateRangePickerProps.Value | null
): string => {
  if (!value) {
    return 'none';
  }

  return value.type === 'relative'
    ? (value.key ?? `last-${Math.abs(value.amount)}-${value.unit}`)
    : 'custom';
};

/**
 * Resolves the results chart x-axis domain from the applied range. With a range
 * (feature flag on) the axis spans the full selected period, so data points,
 * gaps between them, and the tolerance/appetite reference lines all lay out
 * against the whole range rather than shrinking to the data extent (CUS-144).
 * With no range (flag off) `undefined` lets Cloudscape auto-fit to the data,
 * preserving the prior behaviour.
 */
export const resolveChartXDomain = (
  dateRange?: ResolvedDateRange
): [Date, Date] | undefined =>
  dateRange ? [dateRange.start, dateRange.end] : undefined;
